/**
 * DataModelCard — the widget's DATA, as the third thing in the strip.
 *
 * The graph on the right answers "what is this module wired to". Plenty of
 * modules are wired to nothing and still render real content (a MacroCardRow
 * has no connections at all) — they read a SELECTOR. This card is where that
 * lives: what the host actually stored, how those rows fold and derive, and
 * which of those stages a view binds.
 *
 * TWO COLUMNS, because "real vs derived" is the first question a reader has and
 * one flat tree could not answer it:
 *
 *   STORED                          DERIVED
 *   key id · latest-wins            11 selectors · 2 bound to UI
 *   creation      8 stored rows     sessionFold = fold creation        8 rows → 3 items
 *     Angel scene        2026-07-…    sessionWithMedia = derive mediaState ⟵ sessionFold
 *       media_url ⧉ clip.mp4          sessionWithCover = derive firstPhoto ⟵ sessionWithMedia
 *       …                               → sessionWall            ← the UI binding
 *
 * LEFT is ground truth: the record kinds and the RAW feed entries under each —
 * what the host persisted, before any fold. Nothing in the pipeline can explain
 * a row that isn't there, so the rows come first and the fold rule sits over
 * them. RIGHT is the pipeline: every selector in dependency order with its real
 * evaluated output (buckets → items → attributes) hanging off it. The fold's
 * dedup is stated where it happens ("6 rows → 3 items"), so the two columns
 * join up at the number rather than by inference.
 *
 * A selector no module reads is PLUMBING — `sessionWithMedia` exists so `sessionWithCover`
 * can exist — and is drawn muted. A selector a module binds carries that module
 * as a badge (`→ sessionWall`), which is also the jump into the focus zone. So
 * the column separates UI-facing stages from glue at a glance, which is the
 * other question the flat tree could not answer.
 *
 * It is deliberately WIDER than a `PhoneCard` — it is not a phone, it is a
 * titled panel — but exactly as TALL, and it reserves the same zone band, so
 * the strip's frames stay on one baseline and the wires anchored inside them
 * are measured against a row that does not step. Each column scrolls on its own.
 *
 * Mock mode used to draw the model DRY — kinds, fold, a selector list, no rows
 * — while the phone beside it was full of mock medicines. Same widget, two
 * truths, and the injected lifecycle states were invisible here. It is the same
 * data in both modes now; only the feed differs, and My data never falls back
 * to the mock one.
 *
 * Every row in both columns is selectable, and a selection is what connects the
 * data zone to the focus zone: it lights the selectors that read it, it names
 * every module that RENDERS it, and it becomes the anchor the explorer's left
 * wires start from — whichever column it is in.
 *
 * A pick lights the pipeline at TWO grains, because a row is a stage in a flow
 * and the flow is the answer: the SELECTORS it passes through (`selLit`), and
 * the ROWS at those stages that are this same row under another name — the
 * stored writes it folded from, the derived items and bucket members it becomes
 * (`feedersOf` / `flowOf`, matched on the identities a row answers to). Picking
 * `Ibuprofen` on the right lights the two writes on the left that made it.
 *
 * Those lit rows are HIGHLIGHTS, never wires. The two columns scroll
 * independently, so a line drawn between them would be anchored to two moving
 * points and mostly clipped; the wires stay in the explorer, data zone → focus
 * zone, where both ends sit in frames that move together.
 *
 * A pick DOES move the focus — onto the FIRST thing it connects to, and that
 * word is the whole of the difference. The bias the fan-out exists to remove
 * was "whichever module is focused wins": the pick resolved its one endpoint
 * THROUGH the focus, so every later pick resolved against the last answer. Here
 * the wire SET is computed without a focus term at all, and the selection then
 * follows the set's first focus-zone endpoint (grid-laying modules lead). Same
 * click, same wires, whatever was focused before — and the reader is left
 * looking at the thing they just asked about instead of at nothing.
 *
 * The reverse direction is NOT a pick. Focusing a card used to auto-pick its
 * row and re-run the outward fan, so focusing one clip drew wires to every
 * sibling card that row also surfaces in. A focus click converges instead: the
 * rows that FEED the focused thing, at its own level, each wired onto it
 * (`feedsOfFocus`). The tree lights the same set it wires.
 *
 * A CARD is not the only focus that names a row. A DETAIL-PAGE module names one
 * too — scene-studio's `stillStage` is a Carousel bound to `focusEntry.images`,
 * i.e. to the creation `page_args` opened the page on — and because that is not
 * a selector NAME, this card used to report it as reading nothing and the strip
 * drew it no data connection at all. `boundItemRow` resolves the page's row out
 * of the same tree, and from there it is treated exactly like a focused card's
 * row: selected, revealed, lit, and wired into the module that renders it.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectorsRead, selectorLineage, boundItemBinding } from './focusModel';
import {
  buildPipelineTree, selectorsForKind, findItemNode, readersOf, feedersOf, flowOf, feedsOfFocus,
  boundItemRow,
} from './dataTree';
import { scrollWithin } from './scrollWithin';
import { CARD_W, CARD_H, ZoneLabel } from './PhoneCard';
import { ZONE_LABEL_H, ZONE_GAP } from './phoneMetrics';

const C = { text: '#1c1c1e', dim: '#6e6e73', line: '#e2e2ea', accent: '#6E59F2', data: '#b45309', good: '#1a7a4d' };

const MONO = 'ui-monospace, SFMono-Regular, monospace';

const SEL_BG = '#fff7ec';

/**
 * TWO phone cards side by side with the strip's own zone gap between them.
 * The panel has to hold two trees of `key value` rows; at a phone's width one
 * of them would be a 100px gutter, and the whole point of the split is that
 * both sides stay readable. Sized off the phone rather than a free number so
 * the strip keeps one rhythm.
 */
export const DATA_CARD_W = CARD_W * 2 + ZONE_GAP;

/** One selector as `op …`, read off the selector object itself. */
function describe(spec) {
  if (!spec || typeof spec !== 'object') return String(spec ?? '');
  switch (spec.op) {
    case 'fold': return `fold ${spec.kind || ''}`.trim();
    case 'latest': return `latest ${spec.kind || ''}`.trim();
    case 'filter': return `filter ${spec.from || ''}${spec.where ? ` where ${typeof spec.where === 'string' ? spec.where : JSON.stringify(spec.where)}` : ''}`;
    case 'group': return `group ${spec.from || ''} by ${spec.by || ''}${spec.buckets ? ` (${spec.buckets.length})` : ''}`;
    case 'derive': return `derive ${spec.expr || ''}${spec.of ? ` ⟵ ${spec.of}` : ''}`;
    default: return spec.op ? `${spec.op}` : '';
  }
}

const LABEL_STYLE = {
  fontSize: 9, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.dim,
};

/**
 * A column: its one-word name, a line of METERING under it (the fold rule, the
 * selector counts — data, never a caption explaining the column), and a body
 * that scrolls on its own. The head is outside the scroller so it stays put:
 * with two independent scrollers, a heading that scrolls away leaves the reader
 * with no way to tell which side they are looking at.
 */
function Column({ label, meter, testId, first = false, children }) {
  return (
    <div style={{
      flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column',
      borderLeft: first ? 'none' : `1px solid ${C.line}`,
    }}>
      <div style={{ padding: '8px 10px 6px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={LABEL_STYLE}>{label}</div>
        {meter ? (
          <div style={{ fontSize: 9.5, color: C.dim, fontFamily: MONO, lineHeight: 1.45 }}>{meter}</div>
        ) : null}
      </div>
      <div
        data-vi-scroll="1"
        data-testid={testId}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
          padding: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: 2,
        }}
      >{children}</div>
    </div>
  );
}

/**
 * One row of the data tree. The chevron and the row are separate buttons: a
 * click on the label SELECTS (which moves the wires), a click on the chevron
 * only opens — otherwise expanding a kind would silently retarget them.
 *
 * `anchorRef` is the explorer's `rowRef` on whichever row the wires start from,
 * and the CALLER decides which that is: the selected row (in EITHER column), or
 * — with nothing picked — the selector the focused module reads.
 *
 * A `tail` is ALWAYS a second line, and that is not a per-row judgement call:
 * a column here is about a phone's width — some twenty monospace characters
 * once the indent and the chevron are paid for. Nothing meaningful fits beside
 * a label at that width, and the label is what loses: "Children’s Ibuprofen"
 * ellipsised away to make room for "expiring", and `shelves = group medsStatus
 * by category` wrapped into a 70px gutter so "3 buckets" could sit at the top
 * right. So the label owns line one and the tail sits under it as auxiliary
 * meta. Rows with no tail — an attribute's `key value` — stay the single line
 * they already are.
 *
 * `dim` is the focus rule (this row is off the lit lineage). `muted` is the
 * PLUMBING rule (nothing renders this selector). They are separate on purpose:
 * a stage can be glue and still be lit, and the two greys must not be the same
 * grey or "is anything looking at this?" stops being readable.
 *
 * `lit` is the FAN-OUT rule: this row is the picked row at another stage of the
 * pipeline. It takes the selection's own tint and stops there — the border is
 * what says "this is the one you clicked", so the family reads as one flow with
 * a single origin rather than as five selections.
 */
function TreeRow({
  nodeId, depth = 0, expandable = false, expanded = false, selected = false,
  onToggle, onSelect, anchorRef = null, label, tail = null,
  dim = false, muted = false, lit = false, extra = null,
}) {
  const stacked = Boolean(tail);
  return (
    <div style={{
      display: 'flex', alignItems: stacked ? 'flex-start' : 'center', gap: 1, paddingLeft: depth * 10,
    }}>
      {expandable ? (
        <button
          type="button"
          aria-label={expanded ? `collapse ${nodeId}` : `expand ${nodeId}`}
          onClick={onToggle}
          style={{
            flex: '0 0 auto', width: 13, height: 15, padding: 0, lineHeight: '13px',
            background: 'none', border: 'none', cursor: 'pointer', color: C.dim, fontSize: 9,
          }}
        >{expanded ? '▾' : '▸'}</button>
      ) : <span aria-hidden="true" style={{ flex: '0 0 auto', width: 13 }} />}
      <button
        type="button"
        ref={anchorRef}
        data-tree-node={nodeId}
        data-selected={selected ? '1' : undefined}
        data-row-lit={lit && !selected ? '1' : undefined}
        {...extra}
        onClick={onSelect}
        style={{
          flex: 1, minWidth: 0, display: 'flex',
          flexDirection: stacked ? 'column' : 'row',
          alignItems: stacked ? 'stretch' : 'baseline', gap: stacked ? 1 : 6,
          font: 'inherit', fontFamily: MONO, fontSize: 10,
          textAlign: 'left', cursor: 'pointer', padding: '2px 6px', borderRadius: 5,
          background: selected || lit ? SEL_BG : '#f7f7fa',
          border: `1px solid ${selected ? C.data : 'transparent'}`,
          color: C.text, opacity: dim ? 0.45 : muted ? 0.62 : 1,
        }}
      >
        <span style={stacked
          ? { minWidth: 0, wordBreak: 'break-word' }
          : { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >{label}</span>
        {tail ? (
          <span style={stacked
            ? { color: C.dim, fontSize: 9.5, lineHeight: 1.3 }
            : { marginLeft: 'auto', flex: '0 0 auto', color: C.dim, fontSize: 9.5 }}
          >{tail}</span>
        ) : null}
      </button>
    </div>
  );
}

/**
 * The MODULE that binds this selector, as a jump into the focus zone.
 *
 * It sits on its own line under the row rather than beside the label — the row
 * is one button (a button inside a button is not a thing), and at this width a
 * badge in the gutter would eat the selector name it is describing.
 */
function BindBadges({ ids, depth, onFocusModule }) {
  if (!ids.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: depth * 10 + 14 }}>
      {ids.map((id) => (
        <button
          key={id}
          type="button"
          data-binds={id}
          title={`Focus ${id}`}
          onClick={() => onFocusModule?.(id)}
          style={{
            font: 'inherit', fontFamily: MONO, fontSize: 9, lineHeight: '15px',
            padding: '0 6px', borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${C.accent}`, background: '#f3f1ff', color: C.accent,
          }}
        >→ {id}</button>
      ))}
    </div>
  );
}

export default function DataModelCard({
  blueprint, focus = null, cardRef = null, rowRef = null,
  dataMode = 'mock', feed = null, live = null, onSelect = null, onFocusModule = null,
  onPicked = null, renderers = [], focusItem = null, pageArgs = null, zone = null,
}) {
  const data = blueprint?.data || {};
  const fold = data.fold || {};
  // MEMOISED, not recomputed: these feed the converging model this card reports
  // upward, and a fresh array every render would make that report a new object
  // every render — an effect that writes the explorer's state on a loop.
  const reads = useMemo(() => selectorsRead(blueprint, focus), [blueprint, focus]);
  const lit = useMemo(() => selectorLineage(blueprint, reads), [blueprint, reads]);
  const primary = reads[0] || null;
  const isUser = dataMode === 'user';

  // The pipeline, EVALUATED against the feed the STRIP is rendering — the same
  // call the runtime makes, so the numbers in the tree are the numbers on the
  // phone. My data never falls back to the mock feed: an empty instance stays
  // empty here exactly as it does on the page.
  const treeFeed = isUser ? live?.feed || null : feed;
  const tree = useMemo(
    () => (treeFeed ? buildPipelineTree(blueprint, treeFeed) : null),
    [blueprint, treeFeed],
  );

  // WHICH SELECTORS THE UI ACTUALLY BINDS. Everything else in the right column
  // is plumbing — a stage that exists so a later one can, rendered by nothing.
  // `readersOf` is the reverse of `selectorsRead`, so this is the same wiring
  // the explorer resolves its data wire through, read the other way.
  const readers = useMemo(() => readersOf(blueprint), [blueprint]);
  const boundBy = useCallback(
    (name) => (readers.get(name) || []).filter((id) => blueprint?.components?.[id]?.type !== 'host'),
    [blueprint, readers],
  );
  const uiBoundCount = useMemo(
    () => (tree?.nodes || []).filter((n) => boundBy(n.name).length).length,
    [tree, boundBy],
  );

  // THE REVERSE DIRECTION: focusing a laid-out card (`<grid>__card__<itemId>`)
  // is a selection of its DATA. The tree opens onto that row and marks it, and
  // reporting it upward is what makes the wires land on it — so the link reads
  // both ways: pick a row → its modules light; focus a card → every row that
  // feeds it lights (its stored writes and its derived stages alike).
  // `row` rides along with every ITEM-level pick: the keys say what a card can
  // be addressed by, the row says what it IS. A consumer that has to open a
  // page ON this row (the explorer's host-opens-a-page card) needs the row's
  // own creation/session id, which is not always the key its grid laid the card
  // out by — a SessionCollection card is keyed by the SESSION.
  const cardRow = useMemo(() => findItemNode(tree, focusItem), [tree, focusItem]);
  /**
   * …AND THE SAME CARD READ THROUGH ITS OWN GRID.
   *
   * `cardRow` above is the row's EARLIEST appearance, which is what the wires
   * want: they draw the row's lineage from the stored write forward. But a key
   * can name two different rows at different stages — scene-studio's
   * `s-heavenly` session is the CLIP in the Clips rail's fold and the later
   * still in the wall's — and a consumer that must say what TAPPING this card
   * opens needs the row this card is actually drawn from, which is the one its
   * own grid folded. The card id carries that grid (`clipRail__card__…`), so
   * its `bind` names the selector to ask first.
   */
  const cardSelector = useMemo(() => {
    const i = String(focus || '').indexOf('__card__');
    const grid = i < 0 ? null : blueprint?.components?.[String(focus).slice(0, i)];
    const raw = typeof grid?.bind === 'string' ? grid.bind : grid?.bind?.bind;
    return typeof raw === 'string' ? raw.split('.')[0].trim() : null;
  }, [blueprint, focus]);
  const ownRow = useMemo(
    () => (cardSelector ? findItemNode(tree, focusItem, cardSelector) : cardRow),
    [cardRow, cardSelector, tree, focusItem],
  );
  /**
   * …AND THE OTHER WAY A FOCUS NAMES A ROW: a DETAIL-PAGE module. It reads
   * `focusEntry.*` (scene-studio's carousel binds `focusEntry.images`), which
   * is not a selector, so `selectorsRead` came back empty and the module drew
   * no data connection at all while plainly rendering the focused creation.
   * The row is the one `page_args` opened the page on — the same row the phone
   * beside it is drawing — and from here on it is treated exactly like a
   * focused card's row: selected, revealed, lit, and wired INTO the module.
   */
  const bound = useMemo(() => {
    const binding = boundItemBinding(blueprint, focus);
    return binding ? boundItemRow(tree, pageArgs, binding) : null;
  }, [blueprint, tree, focus, pageArgs]);
  const found = cardRow || bound;
  const autoPick = useMemo(() => (found ? {
    id: found.id, level: 'item', kind: found.node.kind, selector: found.node.name,
    itemKeys: found.item.keys, label: found.item.label, row: found.item.row,
  } : null), [found]);
  const revealed = useMemo(() => new Set(found ? found.reveal : []), [found]);

  // Keyed BY the focus it belongs to, the way the explorer keys its active
  // connection: focusing a module asks a NEW question, so the old data pick is
  // dropped and the wires follow the focus. Both drive the explorer's left
  // wires and only ONE can own them — last one wins, and no effect is needed to
  // say so.
  //
  // A pick DOES move the focus again (onto the first thing it connects to), so
  // the key it is written under is the focus that pick is about to CAUSE, not
  // the one it was clicked under — `onPicked` resolves the endpoints and sets
  // the focus synchronously, in the click, and hands back where it landed.
  // Keying it under the old focus is what made a pick destroy itself.
  const [picked, setPicked] = useState({ focus: null, value: null });
  const kept = picked.value && picked.focus === focus ? picked.value : null;
  const sel = kept || autoPick;

  /**
   * WHAT FEEDS THE FOCUS, when no row is picked — the CONVERGING model, which
   * the explorer draws as wires ending ON the focused node. It is the tree's
   * own reading of the focus (`found` is the card's row), so the rows that
   * light and the rows that are wired are one set.
   */
  const converge = useMemo(
    () => (kept ? [] : feedsOfFocus(blueprint, tree, focus, found)),
    [blueprint, tree, focus, found, kept],
  );
  // ONE report, so the explorer's left wires have a single source of truth: the
  // user's pick (or nothing), the evaluated tree the row ids belong to, and the
  // converging rows for a focus with no pick.
  //
  // `cardRow` is reported BESIDE `sel` because they answer different questions
  // and are not always the same row: `sel` is what the reader is looking at,
  // `cardRow` is what the FOCUSED CARD is (`ownRow` — its own grid's fold).
  // They diverge two ways, and a consumer that has to say what TAPPING the card
  // opens (the explorer's host-opens-a-page card) is wrong under both: a pick
  // that lands on a card folding several rows, and a key two grids fold
  // differently. Null when no card is focused.
  const report = useMemo(
    () => ({ sel, pick: kept, cardRow: ownRow?.item?.row || null, tree, converge }),
    [sel, kept, ownRow, tree, converge],
  );
  useEffect(() => { onSelect?.(report); }, [onSelect, report]);

  // WHAT `rowRef` IS ON, this render: the picked row, or — with nothing picked
  // — the selector the focused module reads (see `anchored` in renderSelector).
  // The reveal below keys on this rather than on the pick alone, so focusing a
  // module that nobody picked a row for still brings its `reads <selector>` row
  // into view: every way of selecting lands the same.
  const anchorId = sel?.id || (primary ? `sel:${primary}` : null);

  /**
   * REVEAL THE ANCHORED ROW. The tree is two independently scrolling columns of
   * a hundred-odd rows; a selection arriving from the OTHER side of the strip —
   * a module tapped in the focus zone, a card tapped in the live preview — lands
   * on a row that is very often below the fold, so the highlight and the wire's
   * start point were both invisible.
   *
   * The row's own `[data-vi-scroll]` column is the only thing that moves
   * (`scrollWithin`, never `scrollIntoView`, which would scroll the strip and
   * the studio page with it). A row inside a CLOSED node is not rendered at all
   * and `rowRef` is null — `revealed` (from `found.reveal`) is what opens the
   * ancestors first, so by the time this runs the row exists.
   */
  useLayoutEffect(() => {
    const row = rowRef?.current;
    if (!row || !anchorId) return;
    scrollWithin(row.closest?.('[data-vi-scroll]'), row);
  }, [rowRef, anchorId, tree]);

  // Open/closed by exception: kinds and the FOLD selectors start open (that is
  // where the rows and the dedup show), everything else closed, and this set
  // holds every node flipped by hand.
  const [flipped, setFlipped] = useState(() => new Set());
  const isOpen = useCallback((id, dflt) => (flipped.has(id) ? !dflt : dflt), [flipped]);
  const toggle = useCallback((id) => setFlipped((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  }), []);

  // A pick lights the pipeline: anything PRE-SELECTOR (a kind, or one of its
  // raw stored rows) lights every selector that reads that kind; a selector or
  // one of its outputs lights its own lineage.
  const selLit = useMemo(() => {
    if (!sel) return new Set();
    if (!sel.selector) return selectorsForKind(blueprint, sel.kind);
    return selectorLineage(blueprint, [sel.selector]);
  }, [blueprint, sel]);

  /**
   * THE SAME ROW AT EVERY OTHER STAGE — both directions at once, which is the
   * one set the two columns cannot show on their own:
   *   · backward, the STORED writes it folded from (`feedersOf`), so picking a
   *     derived item lights the raw rows that made it;
   *   · forward, every DERIVED row it becomes (`flowOf`) — the fold's item, the
   *     annotated one, the member of a bucket.
   * A selector/kind/bucket pick carries no identity, so it lights nothing here
   * and the selector lighting above is the whole reading, as before.
   */
  const rowLit = useMemo(() => {
    if (!sel?.itemKeys?.length) return new Set();
    const item = { keys: sel.itemKeys, row: sel.row };
    // the fold SPEC off the blueprint, not the local `fold` — that one is
    // rebuilt every render and would defeat the memo
    const rule = blueprint?.data?.fold || null;
    return new Set([...feedersOf(tree, item, rule), ...flowOf(tree, item, rule)]);
  }, [blueprint, tree, sel]);

  /**
   * A USER CLICK on a row. Clicking the row that is already selected releases
   * it; `autoPick` (a focused card reverse-mapped onto its row) then speaks for
   * the selection again, from the other side.
   *
   * THE CLICK IS WHERE THE FOCUS MOVES, and the only place it can: `onPicked`
   * resolves the pick's endpoints against the committed DOM and focuses the
   * FIRST one that is a focus-zone node — deterministic (grid-laying modules
   * lead), never "whichever is focused now" — or clears the focus when the row
   * reaches no module at all. A RELEASE moves nothing: letting go of a row is
   * not an answer to "what should I be looking at".
   */
  const pick = (next) => {
    const value = kept?.id === next.id ? null : next;
    // no handler (the card standing on its own) → the focus cannot move, so the
    // pick belongs to the focus it was clicked under, as it always did
    const landed = value && onPicked ? onPicked(value, tree) ?? null : focus;
    setPicked({ focus: landed, value });
  };

  /** One row's attributes, under whatever node owns them. */
  const renderRowAttrs = (item, base, depth, { kind, selector }) => item.attrs.map((a) => {
    const attrId = `${base}:${a.key}`;
    const onAttr = sel?.id === attrId;
    return (
      <TreeRow
        key={attrId}
        nodeId={attrId}
        depth={depth}
        selected={onAttr}
        anchorRef={onAttr ? rowRef : null}
        onSelect={() => pick({
          id: attrId, level: 'attr', kind, selector,
          itemKeys: item.keys, label: `${item.label}.${a.key}`, row: item.row,
        })}
        label={<><span style={{ color: C.dim }}>{a.key}</span>{' '}<span style={{ color: C.data }}>{a.value}</span></>}
      />
    );
  });

  /** The item rows + their attributes, under whatever selector node owns them. */
  const renderItems = (items, base, depth, node) => items.map((item) => {
    const itemId = `${base}:item:${item.id}`;
    const open = isOpen(itemId, false);
    const on = sel?.id === itemId;
    return (
      <div key={itemId} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TreeRow
          nodeId={itemId}
          depth={depth}
          expandable={Boolean(item.attrs.length)}
          expanded={open}
          selected={on}
          lit={rowLit.has(itemId)}
          anchorRef={on ? rowRef : null}
          onToggle={() => toggle(itemId)}
          onSelect={() => pick({
            id: itemId, level: 'item', kind: node.kind, selector: node.name,
            itemKeys: item.keys, label: item.label, row: item.row,
          })}
          label={item.label}
          // the lifecycle the phone is drawing it in wins over the date: a row
          // that is going away is the thing you came to this zone to see
          tail={item.state
            ? <span data-item-state={item.state} style={{ color: C.data, fontWeight: 700 }}>{item.state}</span>
            : item.when}
        />
        {open ? renderRowAttrs(item, itemId, depth + 1, { kind: node.kind, selector: node.name }) : null}
      </div>
    );
  });

  /**
   * A snapshot selector's OWN fields, shown directly beneath it — it is one
   * object, not a one-row list, so there is no "item" to nest them under.
   */
  const renderAttrs = (attrs, base, depth, node) => (attrs || []).map((a) => {
    const attrId = `${base}:${a.key}`;
    return (
      <TreeRow
        key={attrId}
        nodeId={attrId}
        depth={depth}
        selected={sel?.id === attrId}
        anchorRef={sel?.id === attrId ? rowRef : null}
        onSelect={() => pick({
          id: attrId, level: 'attr', kind: node.kind, selector: node.name,
          label: `${node.name}.${a.key}`,
        })}
        label={<><span style={{ color: C.dim }}>{a.key}</span>{' '}<span style={{ color: C.data }}>{a.value}</span></>}
      />
    );
  });

  /**
   * ONE RAW STORED ROW — a feed entry, normalised the way the fold sees it and
   * nothing more. It is pre-selector, so its pick carries `selector: null` and
   * lights every selector that rests on its kind; the explorer seeds its
   * fan-out the same way (`selectorsForKind`), so the wires still land on every
   * surface that ends up drawing this row.
   */
  const renderRaw = (kind, rows, depth) => rows.map((item, i) => {
    const rowId = `raw:${kind}:${i}`;
    const open = isOpen(rowId, false);
    const on = sel?.id === rowId;
    return (
      <div key={rowId} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TreeRow
          nodeId={rowId}
          depth={depth}
          expandable={Boolean(item.attrs.length)}
          expanded={open}
          selected={on}
          lit={rowLit.has(rowId)}
          anchorRef={on ? rowRef : null}
          onToggle={() => toggle(rowId)}
          onSelect={() => pick({
            id: rowId, level: 'item', kind, selector: null,
            itemKeys: item.keys, label: item.label, row: item.row,
          })}
          label={item.label}
          tail={item.state
            ? <span data-item-state={item.state} style={{ color: C.data, fontWeight: 700 }}>{item.state}</span>
            : item.when}
        />
        {open ? renderRowAttrs(item, rowId, depth + 1, { kind, selector: null }) : null}
      </div>
    );
  });

  /** One selector on the spine, with its REAL output as children. */
  const renderSelector = (node, depth) => {
    const id = `sel:${node.name}`;
    const byFocus = lit.has(node.name);
    const on = byFocus || selLit.has(node.name);
    const open = isOpen(id, node.depth === 0 || byFocus || revealed.has(id));
    const selected = sel?.id === id;
    const bound = boundBy(node.name);
    // With nothing picked the wire starts at what the FOCUS reads — that is the
    // "reads <selector>" reading, and it must land on that row, not on the card.
    const anchored = selected || (!sel && node.name === primary);
    return (
      <div key={id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TreeRow
          nodeId={id}
          depth={depth}
          expandable={node.shape !== 'scalar'}
          expanded={open}
          selected={selected}
          anchorRef={anchored ? rowRef : null}
          dim={Boolean(focus || sel) && !on && !selected}
          muted={!bound.length && !on && !selected}
          extra={{
            'data-selector': node.name,
            'data-lit': on ? '1' : undefined,
            'data-ui-bound': bound.length ? '1' : undefined,
          }}
          onToggle={() => toggle(id)}
          onSelect={() => pick({
            id, level: 'selector', kind: node.kind, selector: node.name, label: node.name,
          })}
          label={<><b style={{ color: on ? C.accent : C.text }}>{node.name}</b>
            <span style={{ color: C.dim }}> = </span>
            <span style={{ color: C.data }}>{describe(node.spec)}</span></>}
          tail={node.summary}
        />
        <BindBadges ids={bound} depth={depth} onFocusModule={onFocusModule} />
        {open && node.shape === 'groups' ? node.groups.map((b) => {
          const bid = `${id}:bucket:${b.id}`;
          const bopen = isOpen(bid, revealed.has(bid));
          const onBucket = sel?.id === bid;
          return (
            <div key={bid} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TreeRow
                nodeId={bid}
                depth={depth + 1}
                expandable={Boolean(b.items.length)}
                expanded={bopen}
                selected={onBucket}
                anchorRef={onBucket ? rowRef : null}
                onToggle={() => toggle(bid)}
                onSelect={() => pick({
                  id: bid, level: 'bucket', kind: node.kind, selector: node.name,
                  label: `${node.name} · ${b.label}`,
                })}
                label={b.label}
                tail={`${b.items.length} items`}
              />
              {bopen ? renderItems(b.items, bid, depth + 2, node) : null}
            </div>
          );
        }) : null}
        {open && node.shape === 'items' ? renderItems(node.items, id, depth + 1, node) : null}
        {open && node.shape === 'object' ? renderAttrs(node.attrs, id, depth + 1, node) : null}
      </div>
    );
  };

  // No tree, no columns: until the fetch lands there is nothing real to hang off
  // the pipeline, and drawing the selectors dry would be the disconnected view
  // this card exists to replace.
  const emptyNote = tree?.kinds?.length ? null
    : live?.state === 'error' ? live.error
      : live?.state === 'not-adopted'
        ? 'No instance yet — use the + to capture, then your real rows flow through this pipeline.'
        : tree ? 'This blueprint declares no record types — nothing flows yet.'
          : 'Loading your data…';

  const settings = Object.entries(data.settings || {}).slice(0, 4);

  return (
    <div
      data-card="1"
      data-testid="data-model-card"
      style={{
        flex: '0 0 auto', width: DATA_CARD_W, display: 'flex', flexDirection: 'column', gap: 8,
        // the strip's zone band — the same reservation every PhoneCard makes,
        // so this panel's top edge sits on the phones' baseline
        position: 'relative', paddingTop: ZONE_LABEL_H,
      }}
    >
      <div
        ref={cardRef}
        style={{
          width: DATA_CARD_W, height: CARD_H, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column',
          border: `1px solid ${C.line}`, borderRadius: 12, background: '#fff', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '10px 12px', borderBottom: `1px solid ${C.line}`,
          fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.text,
        }}>Widget data{isUser ? ' · My data' : ' · Mock data'}</div>

        {emptyNote ? (
          <div style={{
            flex: 1, minHeight: 0, padding: 12, fontSize: 10.5, lineHeight: 1.45,
            color: live?.state === 'error' ? '#b3261e' : C.dim,
          }}>{emptyNote}</div>
        ) : (
          <div
            data-testid="data-tree"
            style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}
          >
            {/* LEFT — what the host really persisted, with the fold RULE over
                it (the "N rows → M items" printed at each fold on the right is
                the same statement with the numbers in it) */}
            <Column
              label="Stored"
              first
              testId="data-col-stored"
              meter={<>key <b>{fold.key || 'id'}</b>{fold.fallback_key ? <> → <b>{fold.fallback_key}</b></> : null}
                {' · '}{fold.strategy || 'latest-wins'}
                {fold.correction_key ? <> · corrections via <b>{fold.correction_key}</b></> : null}
                {fold.drop_on?.length ? <> · drop on {fold.drop_on.join(', ')}</> : null}</>}
            >
              {(tree?.kinds || []).map(({ kind, rows, pending, raw }) => {
                const kid = `kind:${kind}`;
                const open = isOpen(kid, true);
                const onKind = sel?.id === kid;
                return (
                  <div key={kid} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TreeRow
                      nodeId={kid}
                      expandable={Boolean(raw?.length)}
                      expanded={open}
                      selected={onKind}
                      anchorRef={onKind ? rowRef : null}
                      onToggle={() => toggle(kid)}
                      onSelect={() => pick({ id: kid, level: 'kind', kind, selector: null, label: kind })}
                      label={<b>{kind}</b>}
                      // an in-flight capture is not a stored row and folds
                      // into nothing — it is counted, never listed as a row
                      tail={pending ? `${rows} stored rows · ${pending} pending` : `${rows} stored rows`}
                    />
                    {open ? renderRaw(kind, raw || [], 1) : null}
                  </div>
                );
              })}
              {settings.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 8 }}>
                  <div style={LABEL_STYLE}>Settings</div>
                  <div style={{ fontSize: 10, color: C.dim, fontFamily: MONO, lineHeight: 1.5 }}>
                    {settings.map(([k, v]) => (
                      <div key={k}>{k}: {Array.isArray(v) ? v.join(' · ') : String(v)}</div>
                    ))}
                  </div>
                </div>
              ) : null}
            </Column>

            {/* RIGHT — the pipeline, in dependency order, each stage with its
                real output under it and the module that binds it beside it */}
            <Column
              label="Derived"
              testId="data-col-derived"
              meter={<>{(tree?.nodes || []).length} selectors · {uiBoundCount} bound to UI</>}
            >
              {(tree?.nodes || []).map((n) => renderSelector(n, 0))}
            </Column>
          </div>
        )}

        {/* the AFFORDANCE, not an explanation: `rendered by` is a live jump
            into the focus zone, so it earns its line. With nothing picked
            there is a three-word hint and no paragraph. */}
        <div style={{
          padding: '6px 12px 8px', borderTop: `1px solid ${C.line}`,
          fontSize: 9.5, color: C.dim, lineHeight: 1.45,
        }}>
          {sel ? (
            <>picked <b style={{ color: C.data }}>{sel.label}</b>
              {/* EVERY renderer, not one: the wires are drawn to all of them,
                  and a footer naming a single module was the sentence that
                  disagreed with what the strip was showing. */}
              {renderers.length ? <> — rendered by{' '}
                {renderers.map((id, i) => (
                  <span key={id}>
                    {i ? ' · ' : null}
                    <button
                      type="button"
                      data-rendered-by={id}
                      onClick={() => onFocusModule?.(id)}
                      style={{
                        font: 'inherit', color: C.accent, background: 'none', border: 'none',
                        padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2,
                      }}
                    >{id}</button>
                  </span>
                ))}</> : null}
              {' '}· tap again to release
            </>
          ) : <>tap any row</>}
        </div>
      </div>

      <div style={{ width: DATA_CARD_W, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <code style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>widget data</code>
        <span style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.4 }}>
          {sel
            ? <><b style={{ color: C.data }}>{sel.label}</b> picked{renderers.length ? <> — rendered by <b style={{ color: C.accent }}>{renderers.join(' · ')}</b></> : null}</>
            : focus && primary
              ? <>{focus} reads <b style={{ color: C.accent }}>{primary}</b> — its lineage is lit</>
              : <>stored · derived — the rows, and what every module reads from</>}
        </span>
      </div>
      <ZoneLabel>{zone}</ZoneLabel>
    </div>
  );
}
